// ============================================================================
//  Kur servisi  -  Apollo API uzerinden
// ============================================================================
//  TCMB cagrisi SUNUCU TARAFINDA yapiliyor:
//      GET {REACT_APP_API_URL}/GetTcmbKurlari
//      -> ServisYonetimiController.GetTcmbKurlari
//         -> TcmbKurManager  (ServisYonetimi.Business/Tcmb)
//
//  NEDEN SUNUCUDA:
//  TCMB'nin https://www.tcmb.gov.tr/kurlar/today.xml adresi CORS basligi
//  dondurmuyor, tarayicidan dogrudan okunamiyor. Ayrica sunucuda onbelleklenince
//  tum kullanicilar icin gunde tek cagri yetiyor.
//
//  Servise ulasilamazsa ekran calismaya devam eder: VARSAYILAN_KURLAR devreye
//  girer, kaynak 'varsayilan' doner ve kullaniciya kuru elle kontrol etmesi soylenir.
// ============================================================================

import axios from 'src/utils/axios';

const KUR_ENDPOINT = process.env.REACT_APP_TCMB_KUR_ENDPOINT || '/GetTcmbKurlari';

/** Desteklenen para birimleri. TRY her zaman taban, kuru 1'dir. */
export const PARA_BIRIMLERI = [
  { kod: 'TRY', ad: 'Türk Lirası', simge: '₺', ikon: 'CurrencyLira' },
  { kod: 'USD', ad: 'ABD Doları', simge: '$', ikon: 'AttachMoney' },
  { kod: 'EUR', ad: 'Euro', simge: '€', ikon: 'Euro' },
  { kod: 'GBP', ad: 'İngiliz Sterlini', simge: '£', ikon: 'CurrencyPound' }
];

export const paraBirimi = kod =>
  PARA_BIRIMLERI.find(p => p.kod === kod) || PARA_BIRIMLERI[0];

/** Servise ulasilamadiginda ekran bos kalmasin diye. Kullaniciya uyari gosterilir. */
const VARSAYILAN_KURLAR = { TRY: 1, USD: 43.95, EUR: 47.5, GBP: 55.2 };

let onbellek = null; // { kurlar, tarih, kaynak }

// TCMB gunde bir kez yayin yapiyor. Sunucu zaten onbellekliyor; burada da gun
// damgasiyla saklanip sayfa yenilense bile ayni gun tekrar istek atilmiyor.
// (localStorage kullanilmiyor: App.js mount'ta ve beforeunload'da temizliyor.)
const ONBELLEK_ANAHTARI = 'apollo:tcmbKurlari';

const sayi = deger => {
  const n = parseFloat(String(deger ?? '').replace(',', '.'));
  return Number.isNaN(n) ? 0 : n;
};

const bugun = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

const onbellektenOku = () => {
  try {
    const ham = sessionStorage.getItem(ONBELLEK_ANAHTARI);
    if (!ham) return null;
    const veri = JSON.parse(ham);
    // Sadece bugun alinmis ve gercekten TCMB kaynakli kayit kullanilir.
    if (veri?.cekilmeGunu !== bugun() || veri?.kaynak === 'varsayilan') return null;
    return veri;
  } catch {
    return null;
  }
};

const onbellegeYaz = veri => {
  try {
    sessionStorage.setItem(
      ONBELLEK_ANAHTARI,
      JSON.stringify({ ...veri, cekilmeGunu: bugun() })
    );
  } catch {
    // Depolama kapali/dolu olabilir; kur yine de bellekte tutuluyor.
  }
};

/**
 * API cevabini normalize eder.
 * Sunucu Newtonsoft + DefaultContractResolver kullandigi icin alanlar PascalCase
 * geliyor; yine de iki yazim da kabul ediliyor.
 */
const normalize = veri => {
  const hamKurlar = veri?.Kurlar || veri?.kurlar || {};
  const kurlar = { TRY: 1 };

  Object.keys(hamKurlar).forEach(kod => {
    const deger = sayi(hamKurlar[kod]);
    if (deger > 0) kurlar[kod.toUpperCase()] = deger;
  });

  return {
    kurlar,
    tarih: veri?.Tarih || veri?.tarih || '',
    bultenNo: veri?.BultenNo || veri?.bultenNo || ''
  };
};

/**
 * O gun yayinlanan kurlari getirir.
 *
 * Istek GUNDE BIR KEZ atilir:
 *   1. Bellekteki onbellek (ayni sayfa oturumu)
 *   2. sessionStorage (sayfa yenilense de ayni gun tekrar gidilmez)
 *   3. Ancak ikisi de yoksa API'ye istek
 *
 * @returns {Promise<{kurlar: object, tarih: string, kaynak: 'tcmb'|'varsayilan'}>}
 */
export const getKurlar = async () => {
  if (onbellek) return onbellek;

  const saklanan = onbellektenOku();
  if (saklanan) {
    onbellek = {
      kurlar: saklanan.kurlar,
      tarih: saklanan.tarih,
      kaynak: saklanan.kaynak
    };
    return onbellek;
  }

  try {
    const cevap = await axios.get(KUR_ENDPOINT);
    const cozum = normalize(cevap.data);

    if (!cozum.kurlar.USD) throw new Error('Kur listesi boş geldi.');

    onbellek = { ...cozum, kaynak: 'tcmb' };
    onbellegeYaz(onbellek);
    return onbellek;
  } catch (e) {
    // Ekran calismaya devam etsin; cagiran taraf kaynak='varsayilan' gorup uyarir.
    return { kurlar: { ...VARSAYILAN_KURLAR }, tarih: '', kaynak: 'varsayilan' };
  }
};

/** Test/yenileme icin onbellegi bosaltir (hem bellek hem sessionStorage). */
export const onbellegiTemizle = () => {
  onbellek = null;
  try {
    sessionStorage.removeItem(ONBELLEK_ANAHTARI);
  } catch {
    // yoksay
  }
};

/**
 * TL tutarini secili para birimine cevirir.
 * Katalog fiyatlari TL tutuluyor; kur "1 birim dovizin TL karsiligi".
 */
export const tldenCevir = (tutarTL, kur) => {
  const k = sayi(kur);
  if (!k || k <= 0) return 0;
  return sayi(tutarTL) / k;
};

/** Secili para biriminde tutari formatlar. */
export const tutarFormatla = (tutar, kod = 'TRY') => {
  const pb = paraBirimi(kod);
  const n = sayi(tutar);
  const metin = n.toLocaleString('tr-TR', {
    minimumFractionDigits: kod === 'TRY' ? 0 : 2,
    maximumFractionDigits: 2
  });
  return kod === 'TRY' ? `${metin} ${pb.simge}` : `${pb.simge}${metin}`;
};
